import React, { useCallback, useMemo, useState } from 'react';
import {
  MenuItem,
  MenuItem as MenuItemType,
} from '@hubs101/booking-api-skd-client/lib/types';
import {
  NavContainer,
  Logo,
  NavLinks,
  NavLink,
  MobileMenuIcon,
  MobileMenu,
  MobileNavLink,
  DropdownMenu,
  DropdownItem,
  NavLinkWithDropdown,
  MobileDropdownMenu,
  MobileDropdownItem,
  CloseIconWrapper,
  LogoContainer,
} from './styles';
import weCONECTLogo from '@assets/images/weCONECT_col_RGB.png';
import Icon from '@components/Icon';
import { mappingLinks } from '@composites/EventMenuDesktop';
import LanguageSwitcher from '@composites/LanguageSwitcher';
import { LANGUAGE_SWITCHER_MODE } from '@utils/constants';
import { useMobileWidth } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useLocation } from 'react-router-dom';

const CompanyNav = () => {
  const isMobileWidth = useMobileWidth();
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();

  const isMobile = useMobileWidth();

  const menuItemsItems: any[] = [
    {
      icon: 'icon-home-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: 'home',
      level: '0',
      order: 0,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: '/',
    },
    {
      icon: 'icon-our-product-portfolio-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('our-product-portfolio'),
      level: '0',
      order: 1,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.our-product-portfolio-url'),
    },
    {
      icon: 'icon-career-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('career'),
      level: '0',
      order: 2,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.career-url'),
    },
    {
      icon: 'icon-about-us-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('about-us'),
      level: '0',
      order: 3,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.about-us-url'),
    },

    {
      icon: 'icon-about-us-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('about-us'),
      level: '1',
      order: 4,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.about-us-url'),
    },
    {
      icon: 'icon-our-team-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('our-team'),
      level: '1',
      order: 5,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.our-team-url'),
    },
    {
      icon: 'icon-our-awards-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('our-awards'),
      level: '1',
      order: 6,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.our-awards-url'),
    },
    {
      icon: 'icon-our-awards-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('social-responsibility'),
      level: '1',
      order: 7,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.social-responsibility-url'),
    },
    {
      icon: 'icon-code-of-conduct-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('code-of-conduct'),
      level: '1',
      order: 8,
      styling: 'link',
      subtitle: '',
      type: 'external',
      url: 'https://media.we-conect.com/documents/Code_of_conduct.pdf',
    },
    {
      icon: 'icon-contact-0',
      isDesktopDisabled: false,
      isMobileDisabled: false,
      isTabletDisabled: false,
      label: t('contact'),
      level: '1',
      order: 9,
      styling: 'link',
      subtitle: '',
      type: 'internal',
      url: t('mappable-company-routes.contact-url'),
    },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  );

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const link = useCallback(
    (item: MenuItemType) => {
      switch (item.type) {
        case 'internal':
          return item.url;
        case 'external':
          return item.url;
        default:
          return t(mappingLinks[item.type as keyof typeof mappingLinks]);
      }
    },
    [t]
  );

  const getSecondaryItemsForIndex = (index: number) => {
    return secondaryMenuItems.filter(item => item.index === index);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
    setOpenDropdownIndex(null);
  };

  const toggleDropdown = (index: number) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const sortedMenuItems = menuItemsItems?.toSorted(
    (a: MenuItem, b: MenuItem) => a.order - b.order
  );

  const mainMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => e.level === '0')
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subTitle: item.label,
            link: link(item),
            type: item.type,
            order: item.order,
          };
        }),
    [link, sortedMenuItems]
  );

  const secondaryMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => e.level !== '0')
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subTitle: item.label,
            link: link(item),
            type: item.type,
            order: item.order,
            index: mainMenuItems?.findLastIndex(
              element => element.order < item.order
            ),
          };
        }) ?? [],
    [link, mainMenuItems, sortedMenuItems]
  );

  return (
    <NavContainer>
      <LogoContainer>
        <Logo src={weCONECTLogo} alt="We.Connect Logo" isMobile={isMobile} />
      </LogoContainer>

      <NavLinks>
        {mainMenuItems.map((item, index) => {
          const secondaryItems = getSecondaryItemsForIndex(index);
          const hasSecondaryItems = secondaryItems.length > 0;

          if (hasSecondaryItems) {
            return (
              <NavLinkWithDropdown key={`nav-item-${String(item?.id ?? '')}`}>
                <NavLink
                  className={isActive(item.link) ? 'active' : ''}
                  href={item.link}
                >
                  {t(item.title)}
                </NavLink>
                <DropdownMenu>
                  {secondaryItems.map(subItem => (
                    <DropdownItem
                      key={`sub-item-${String(subItem?.id ?? '')}`}
                      className={isActive(subItem.link) ? 'active' : ''}
                      href={subItem.link}
                      {...(subItem.type === 'external' && {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      })}
                    >
                      {t(subItem.title)}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </NavLinkWithDropdown>
            );
          }

          return (
            <NavLink
              key={`nav-item-${String(item?.id ?? '')}`}
              href={item.link}
              className={isActive(item.link) ? 'active' : ''}
              {...(item.type === 'external' && {
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
            >
              {t(item.title)}
            </NavLink>
          );
        })}
      </NavLinks>

      {!isMobileWidth && (
        <LanguageSwitcher
          mode={
            isMobileWidth
              ? LANGUAGE_SWITCHER_MODE.DARK
              : LANGUAGE_SWITCHER_MODE.LIGHT
          }
        />
      )}

      <MobileMenuIcon onClick={toggleMobileMenu}>☰</MobileMenuIcon>

      {mobileOpen && (
        <MobileMenu>
          <CloseIconWrapper
            onClick={e => {
              e.preventDefault();
              setMobileOpen(false);
            }}
          >
            <Icon
              size={40}
              name="close"
              color={theme.color.mobilityGrey}
              indentLess
            />
          </CloseIconWrapper>

          {mainMenuItems.map((item, index) => {
            const secondaryItems = getSecondaryItemsForIndex(index);
            const hasSecondaryItems = secondaryItems.length > 0;

            return (
              <React.Fragment key={`mobile-nav-item-${String(item?.id ?? '')}`}>
                <MobileNavLink
                  className={isActive(item.link) ? 'active' : ''}
                  href={hasSecondaryItems ? '#' : item.link}
                  onClick={e => {
                    if (hasSecondaryItems) {
                      e.preventDefault();
                      toggleDropdown(index);
                    }
                  }}
                  {...(item.type === 'external' && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                >
                  {t(item.title)}
                  {hasSecondaryItems && (
                    <Icon
                      size={18}
                      color={theme.color.mobilityGrey}
                      name={
                        openDropdownIndex === index
                          ? 'caret_arrow_up'
                          : 'caret_arrow'
                      }
                      indentLess
                    />
                  )}
                </MobileNavLink>

                {hasSecondaryItems && (
                  <MobileDropdownMenu isOpen={openDropdownIndex === index}>
                    {secondaryItems.map(subItem => (
                      <MobileDropdownItem
                        key={`mobile-sub-item-${String(subItem?.id ?? '')}`}
                        className={isActive(subItem.link) ? 'active' : ''}
                        href={subItem.link}
                        {...(subItem.type === 'external' && {
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        })}
                      >
                        {t(subItem.title)}
                      </MobileDropdownItem>
                    ))}
                  </MobileDropdownMenu>
                )}
              </React.Fragment>
            );
          })}

          <LanguageSwitcher mode={LANGUAGE_SWITCHER_MODE.LIGHT} />
        </MobileMenu>
      )}
    </NavContainer>
  );
};

export default CompanyNav;
